import useUserVarStore from "@app/store/useUserVarStore";
import downArrow from "@assets/icons/downArrow.svg";
import ArticleCarousel from "@components/Carousel/ArticleCarousel";
import MainBlock from "@components/Container/MainBlock";
import ArticleParagraph from "@elements/ArticleParagraph/ArticleParagraph";
import Author from "@elements/Author/Author";
import GooglePreferedSourceButton from "@elements/GoogleSourceButton/GooglePreferedSourceButton";
import ShareSocialButton from "@elements/ShareSocial/ShareSocialButton";
import Taboola from "@elements/Taboola/Taboola";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import { StandFirst } from "@elements/Typography/Paragraph";
import { colorConstants, DEFAULT_COLOR } from "@helper/sectionConstants";
import { getDateTime } from "@helper/useDateFormat";
import { isArrayWithElements } from "@helper/utils";
import { useArticleReactions } from "@hooks/useArticleReactions";
import { ProcessedArticleData } from "@transformer/useOsResponse";
import {
  ArticleAdPageTargetingType,
  ArticleAdSlotTargetingType,
} from "@typings/Ads.d";
import { ReactElement, useState } from "react";
import { twMerge } from "tailwind-merge";

const socialIconList = ["whatsapp", "facebook", "x", "copylink"];

export type ArticleContentCardProps = {
  articleData: ProcessedArticleData;
  isInfiniteScrollArticle?: boolean;
  pageNumber: number;
  articlePageTarget?: ArticleAdPageTargetingType[];
  articleSlotTarget?: ArticleAdSlotTargetingType[];
  gsChannels?: string[];
  enableReadMore?: boolean;
  hasTaboola?: boolean;
};

export default function ArticleContentCard({
  articleData,
  pageNumber,
  isInfiniteScrollArticle = false,
  articlePageTarget,
  articleSlotTarget,
  gsChannels,
  enableReadMore = false,
}: ArticleContentCardProps): ReactElement {
  const { userToken } = useUserVarStore();
  const {
    id,
    title,
    kicker,
    standFirst,
    sectionName,
    subSection,
    tags,
    authors,
    createdDate,
    editedDate,
    articleBody,
    urlPath,
    imageGallery,
    relatedStoriesData,
  } = articleData;

  const {
    reactions,
    userReactions,
    handleReactionClick,
    // reactionsError,
    showReactions,
  } = useArticleReactions({
    articleId: id,
    userToken,
    publishDate: createdDate,
    uniqueSectionName: sectionName,
  });

  const pageUrl = `${urlPath}`;
  const [isDateOpen, setDateIsOpen] = useState<boolean>(false);

  const sectionColor =
    sectionName && sectionName in colorConstants
      ? colorConstants[sectionName as keyof typeof colorConstants].color.lightBg
      : "";

  // Toggle the dropdown menu
  const toggleDropdown = () => {
    if (editedDate <= createdDate) return;
    setDateIsOpen((prev) => !prev);
  };

  return (
    <MainBlock pageName={title} showTitle={false} className="xmd:pt-2.5">
      <article className="xmd:my-5 py-3 md:p-0 lg:p-3 xl:p-0 md:mt-3 border-none xmd:border-b border-grey-800 xmd:border-solid">
        <div
          className="text-grey-200 font-opensans text-xs font-bold mx-3 md:mx-5 lg:mx-0 leading-4.5 border-t-2 border-solid p-1 w-max uppercase"
          style={{
            borderColor: sectionColor ? sectionColor : DEFAULT_COLOR,
          }}
        >
          {sectionName}
        </div>
        <div className="font-opensans text-base font-normal text-grey-200 mt-3 mb-1.5 px-3 md:px-5 lg:px-0">
          {kicker}
        </div>

        <Heading
          className="article-headline text-40 font-bold font-primary px-3 md:px-5 lg:px-0"
          style={{
            color: sectionColor ? sectionColor : DEFAULT_COLOR,
          }}
        >
          {title}
        </Heading>

        {standFirst && (
          <StandFirst className="mt-1.5 px-3 md:px-5 lg:px-0">
            {standFirst}
          </StandFirst>
        )}

        <div className="w-full inline-block py-3 px-3 md:px-5 lg:px-0">
          <div className="inline-block">
            <button
              onClick={toggleDropdown}
              className="dropdown-button flex leading-7"
            >
              <Date className="mr-2 font-opensans font-normal text-sm text-grey-500">
                {editedDate > createdDate
                  ? `UPDATED ${getDateTime(editedDate)}`
                  : `PUBLISHED ${getDateTime(createdDate)}`}
              </Date>
              {editedDate && editedDate > createdDate && (
                <img
                  src={downArrow}
                  alt={"dropdown-arrow"}
                  width={16}
                  height={16}
                  loading="lazy"
                  className={twMerge(
                    "duration-500",
                    isDateOpen && "-rotate-180",
                  )}
                />
              )}
            </button>
            <div
              className={twMerge(
                "transition-all duration-500 ease-in-out overflow-hidden",
                isDateOpen ? "max-h-40 opacity-100 mt-2" : "max-h-0 opacity-0",
              )}
            >
              {isDateOpen && editedDate && editedDate > createdDate && (
                <>
                  <div
                    className={twMerge(
                      "font-opensans font-normal text-grey-500 text-sm flex leading-7 items-center",
                    )}
                  >
                    <span className="mr-1">PUBLISHED</span>
                    <Date className="text-sm">{getDateTime(createdDate)}</Date>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="float-right">
            <ShareSocialButton
              socialIconList={socialIconList}
              pagePath={pageUrl}
              className="relative"
            />
          </div>
        </div>

        {isArrayWithElements(imageGallery) && (
          <div className={`article-carousel-wrapper-${id}`}>
            <ArticleCarousel
              renderItems={imageGallery}
              sectionName={sectionName}
            />
          </div>
        )}

        {isArrayWithElements(authors) && authors.length > 0 && (
          <>
            {authors.map((author, index) => (
              <Author
                key={index}
                destination={author.urlPath ? author.urlPath : ""}
                authorSrc={author.authorImage ? author.authorImage.url : ""}
                authorName={author.name}
              />
            ))}
          </>
        )}

        <GooglePreferedSourceButton
          className={twMerge(
            "grid md:inline-flex xmd:justify-end w-full md:w-auto pb-3 px-4 md:float-end",
            isArrayWithElements(authors) &&
              ((authors.length === 1 &&
                !authors.every((author) => author.urlPath)) ||
                authors.filter((author) => author.urlPath).length > 2) &&
              "!w-full xmd:mb-3",
          )}
          pageUrl={pageUrl}
        />

        {isArrayWithElements(articleBody) && (
          <ArticleParagraph
            articleId={id}
            articleTitle={title}
            articleBody={articleBody}
            relatedStories={relatedStoriesData}
            sectionName={sectionName}
            subSectionName={subSection}
            pageUrl={pageUrl}
            dateCreated={createdDate}
            tags={tags}
            authors={authors}
            pageNumber={pageNumber}
            isInfiniteScrollArticle={isInfiniteScrollArticle}
            articlePageTarget={articlePageTarget}
            articleSlotTarget={articleSlotTarget}
            gsChannels={gsChannels}
            enableReadMore={enableReadMore}
            showReactions={showReactions}
            reactions={reactions}
            handleReactionClick={handleReactionClick}
            userReactions={userReactions}
          />
        )}

        {!isInfiniteScrollArticle && (
          <>
            <div className="block bg-grey-50 p-3 xmd:mb-5">
              <div className="block px-3 xl:px-0 md:max-w-screen-xmd xmd:max-w-screen-lg lg:max-w-7xl  m-auto">
                <div className="inline-grid xmd:block xmd:p-0 taboola-container">
                  <Taboola
                    counter={0}
                    pathName={pageUrl}
                    pageType="article"
                    mode="thumbnails-base-widget"
                    container="below-article-thumbnails"
                    placement="Below Article Thumbnails"
                    targetType="mix"
                    isFlush={true}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </article>
    </MainBlock>
  );
}
